import { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldActions } from '../../contexts/field-actions-context';
import { useForm } from '../../contexts/form-context';
import { useTranslation } from '../../contexts/translation-context';
import useDebouncedCallback from '../address-input/use-debounced-callback';
import useIsMounted from '../address-input/use-is-mounted';
import { createEmailDeliverabilityValidator } from './email-availability-validation-service';
import { EmailDeliverabilityStatus } from './email-validation-types';
const DEBOUNCE_MS = 500;
const isEmailDeliverabilityValidationEnabled = (form, fieldId)=>{
    if (!fieldId) {
        return false;
    }
    const field = form?.fields?.find((formField)=>formField.id === fieldId);
    const view = field?.view;
    return Boolean(view?.emailOptions?.deliverabilityValidationEnabled);
};
export const useEmailDeliverabilityValidation = ({ fieldId, email, hasError })=>{
    const form = useForm();
    const { actions } = useFieldActions();
    const validateEmailDeliverability = actions?.validateEmailDeliverability;
    const { t } = useTranslation();
    const message = t('input.email.deliverability-validation.warning');
    const isEnabled = isEmailDeliverabilityValidationEnabled(form, fieldId) && !!validateEmailDeliverability;
    const validator = useMemo(()=>validateEmailDeliverability ? createEmailDeliverabilityValidator(validateEmailDeliverability) : undefined, [
        validateEmailDeliverability
    ]);
    const [status, setStatus] = useState(EmailDeliverabilityStatus.IDLE);
    const requestId = useRef(0);
    const isMounted = useIsMounted();
    const emailRef = useRef(email);
    emailRef.current = email;
    const hasErrorRef = useRef(hasError);
    hasErrorRef.current = hasError;
    const validate = useDebouncedCallback(async ()=>{
        const value = (emailRef.current ?? '').trim();
        if (!isEnabled || !validator || hasErrorRef.current || !value) {
            setStatus(EmailDeliverabilityStatus.IDLE);
            return;
        }
        const currentRequestId = ++requestId.current;
        setStatus(EmailDeliverabilityStatus.CHECKING);
        const isValid = await validator.validate(value);
        if (!isMounted() || currentRequestId !== requestId.current) {
            return;
        }
        setStatus(isValid ? EmailDeliverabilityStatus.VALID : EmailDeliverabilityStatus.INVALID);
    }, [
        isEnabled,
        validator
    ], DEBOUNCE_MS);
    useEffect(()=>{
        validate.cancel();
        requestId.current++;
        setStatus(EmailDeliverabilityStatus.IDLE);
    }, [
        email,
        validate
    ]);
    useEffect(()=>()=>{
            validate.cancel();
            requestId.current++;
        }, [
        validate
    ]);
    return {
        status,
        message,
        validate
    };
};

//# sourceMappingURL=use-email-deliverability-validation.js.map